// Screen 3 — configure the singleton Automated Standup (engine:'ai').
import React, { useState } from 'react';
import { Switch, Select, TimePicker, Button, Input, Avatar as AntAvatar } from 'antd';
import moment from 'moment';
import { MessageOutlined } from '@ant-design/icons';
import { typeByKey, IconTile, DayPills, ReadFromChips, initials, colorFor } from './parts';

const { Option } = Select;

export default function StandupConfig({
  config,
  channels,
  participants,
  onSave,
  onBack,
  saving,
}) {
  const c = config || {};
  const [active, setActive] = useState(c.enabled !== false);
  const [days, setDays] = useState(c.days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [time, setTime] = useState(c.time || '08:00 AM');
  const [timezone, setTimezone] = useState(c.timezone || 'Team default');
  const [channel, setChannel] = useState(c.channel || (channels[0] && channels[0].id));
  const [keepWeb, setKeepWeb] = useState(c.keepWeb !== false);
  const [readFrom, setReadFrom] = useState(c.readFrom || ['jira', 'github', 'slack']);
  const [askDirectly, setAskDirectly] = useState(c.askDirectly || 'unsure');
  const [waitUntil, setWaitUntil] = useState(c.waitUntil || '09:30 AM');
  const [parts, setParts] = useState(() => {
    const m = {};
    (participants || []).forEach((p) => (m[p.id] = p.included !== false));
    return m;
  });
  const [nl, setNl] = useState('');

  const type = typeByKey('standup');

  const save = () =>
    onSave({
      active,
      days,
      time,
      timezone,
      channel,
      keepWeb,
      readFrom,
      askDirectly,
      waitUntil,
      participants: parts,
    });

  return (
    <div className="ck21__inner">
      <span className="ck21__back" onClick={onBack}>
        ‹ All check-ins
      </span>

      <div className="ck21__cfghead">
        <IconTile type={type} large />
        <div style={{ flex: 1 }}>
          <div className="ck21__cfgname">Automated Standup</div>
          <div className="ck21__cfgblurb">
            Writes each person's update from their Jira, GitHub and Slack activity
            and posts it to the channel. The recommended check-in for every team.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600 }}>Active</span>
          <Switch checked={active} onChange={setActive} />
        </div>
      </div>

      <div className="ck21__nlbox">
        <MessageOutlined style={{ color: '#97A0AC', fontSize: 18 }} />
        <Input
          value={nl}
          onChange={(e) => setNl(e.target.value)}
          placeholder={'Or just tell Troopr: "move standup to 9am and skip Fridays"'}
          bordered={false}
        />
        <Button type="primary" style={{ background: '#244AA0', borderColor: '#244AA0' }}>
          Apply
        </Button>
      </div>

      <div className="ck21__section">
        <div className="ck21__sectitle">Schedule</div>
        <div className="ck21__secsub">When the standup runs and posts.</div>
        <div className="ck21__crow">
          <span className="ck21__clabel">Days</span>
          <DayPills value={days} onChange={setDays} />
        </div>
        <div className="ck21__crow">
          <span className="ck21__clabel">Time</span>
          <TimePicker
            use12Hours
            format="hh:mm A"
            value={moment(time, 'hh:mm A')}
            onChange={(m) => setTime(m ? m.format('hh:mm A') : time)}
            allowClear={false}
          />
        </div>
        <div className="ck21__crow">
          <span className="ck21__clabel">Timezone</span>
          <Select
            showSearch
            value={timezone}
            onChange={setTimezone}
            style={{ minWidth: 220 }}
          >
            <Option value="Team default">Team default</Option>
            {moment.tz ? moment.tz.names().map((t) => <Option key={t} value={t}>{t}</Option>) : null}
          </Select>
        </div>
      </div>

      <div className="ck21__section">
        <div className="ck21__sectitle">Delivery</div>
        <div className="ck21__secsub">Where the report lands. The web app copy is always kept.</div>
        <div className="ck21__crow">
          <div>
            <div className="ck21__clabel">Post report to</div>
            <div className="ck21__csub">Defaults to the team channel</div>
          </div>
          <Select value={channel} onChange={setChannel} style={{ minWidth: 220 }}>
            {(channels || []).map((ch) => (
              <Option key={ch.id} value={ch.id}>
                #{ch.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="ck21__crow">
          <div>
            <div className="ck21__clabel">Also keep in web app</div>
            <div className="ck21__csub">Full searchable history</div>
          </div>
          <Switch checked={keepWeb} onChange={setKeepWeb} />
        </div>
      </div>

      <div className="ck21__section">
        <div className="ck21__sectitle">How Troopr writes it</div>
        <div className="ck21__secsub">
          Signals it reads, and when it interrupts a person instead of inferring.
        </div>
        <div className="ck21__crow">
          <span className="ck21__clabel">Read from</span>
          <ReadFromChips value={readFrom} onChange={setReadFrom} />
        </div>
        <div className="ck21__crow">
          <div>
            <div className="ck21__clabel">Ask a person directly</div>
            <div className="ck21__csub">
              Only when it genuinely can't tell what they worked on
            </div>
          </div>
          <Select value={askDirectly} onChange={setAskDirectly} style={{ minWidth: 200 }}>
            <Option value="unsure">Only when unsure</Option>
            <Option value="always">Always</Option>
            <Option value="never">Never</Option>
          </Select>
        </div>
        <div className="ck21__crow">
          <div>
            <div className="ck21__clabel">Wait for replies until</div>
            <div className="ck21__csub">Posts the report at this cutoff regardless</div>
          </div>
          <TimePicker
            use12Hours
            format="hh:mm A"
            value={moment(waitUntil, 'hh:mm A')}
            onChange={(m) => setWaitUntil(m ? m.format('hh:mm A') : waitUntil)}
            allowClear={false}
          />
        </div>
      </div>

      <div className="ck21__section">
        <div className="ck21__sectitle">Participants</div>
        <div className="ck21__secsub">
          Pulled from the team channel and kept in sync. Toggle anyone out who
          shouldn't be included.
        </div>
        {(participants || []).map((p) => (
          <div className="ck21__crow" key={p.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <AntAvatar style={{ background: colorFor(p.id), verticalAlign: 'middle' }} size={34}>
                {initials(p.name)}
              </AntAvatar>
              <span className="ck21__clabel">{p.name}</span>
              {p.area && <span className="ck21__tag">{p.area}</span>}
            </div>
            <Switch
              checked={parts[p.id] !== false}
              onChange={(v) => setParts({ ...parts, [p.id]: v })}
            />
          </div>
        ))}
        {(!participants || participants.length === 0) && (
          <div className="ck21__csub">No participants synced yet.</div>
        )}
      </div>

      <div className="ck21__footer">
        <Button onClick={onBack}>Cancel</Button>
        <Button
          type="primary"
          loading={saving}
          onClick={save}
          style={{ background: '#244AA0', borderColor: '#244AA0' }}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}
